import type {
  Audit,
  Organization,
  PaginatedList,
  PaginationData,
  Patrol,
  Person,
  Session,
} from '@local/common-lib';
import { FlashOn as QuickPatrolIcon } from '@mui/icons-material';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  IconButton,
  Pagination,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { DateTime } from 'luxon';
import type { FC } from 'react';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MiddleSpinner } from '../../components/middle-spinner/index.js';
import { NavLink } from '../../components/nav-link/nav-link.js';
import { CenterLayout } from '../../components/page-layout/index.js';
import { useAxios } from '../../hooks/use-axios.js';
import { useSetTitle } from '../../hooks/use-title.js';
import { ErrorPage } from '../error/index.js';
import { NarrowPatrolList } from '../patrol-list/narrow-view.js';
import { WidePatrolList } from '../patrol-list/wide-view.js';

interface ViewProps {
  auditPagination: PaginationData;
  audits: PaginatedList<Audit.Type>;
  organization: Organization.Type | undefined;
  profile: Person.View;
  recentPatrolPagination: PaginationData;
  recentPatrols: PaginatedList<Patrol.Type>;
  scheduledPatrols: PaginatedList<Patrol.Type>;
  sessionPagination: PaginationData;
  sessions: PaginatedList<Session.Type>;
  setAuditPagination: (pagination: PaginationData) => void;
  setRecentPatrolPagination: (pagination: PaginationData) => void;
  setSessionPagination: (pagination: PaginationData) => void;
}

export const MyRosterPage: FC<{ view?: FC<ViewProps> }> = ({
  view: View = MyRosterView,
}) => {
  const [profile] = useAxios<Person.View>('/api/v1/my/profile');
  const [organization] = useAxios<Organization.Type>('/api/v1/my/organization');
  useSetTitle('My Roster');

  const [sessionPagination, setSessionPagination] = useState({
    limit: 12,
    skip: 0,
  });
  const [sessions] = useAxios<PaginatedList<Session.Type>>(
    '/api/v1/my/sessions',
    useMemo(
      () => ({ params: { ...sessionPagination, sort: '-issued' } }),
      [sessionPagination],
    ),
  );

  const [auditPagination, setAuditPagination] = useState({
    limit: 12,
    skip: 0,
  });
  const [audits] = useAxios<PaginatedList<Audit.Type>>(
    '/api/v1/my/audits',
    useMemo(
      () => ({ params: { ...auditPagination, sort: '-when' } }),
      [auditPagination],
    ),
  );

  const [recentPatrolPagination, setRecentPatrolPagination] = useState({
    limit: 12,
    skip: 0,
  });
  const [recentPatrols] = useAxios<PaginatedList<Patrol.Type>>(
    '/api/v1/my/patrols',
    useMemo(
      () => ({
        params: {
          ...recentPatrolPagination,
          filter: JSON.stringify({
            cancelled: null,
            when: { $lte: DateTime.now() },
          }),
          sort: '-when',
        },
      }),
      [recentPatrolPagination],
    ),
  );

  const [scheduledPatrols] = useAxios<PaginatedList<Patrol.Type>>(
    '/api/v1/my/patrols',
    useMemo(
      () => ({
        params: {
          filter: JSON.stringify({
            cancelled: null,
            when: { $gt: DateTime.now() },
          }),
          limit: 12,
          sort: '-when',
        },
      }),
      [],
    ),
  );

  if (
    audits.loading ||
    profile.loading ||
    recentPatrols.loading ||
    scheduledPatrols.loading ||
    sessions.loading
  ) {
    return <MiddleSpinner />;
  }

  if (audits.error) return <ErrorPage error={audits.error} />;
  if (profile.error) return <ErrorPage error={profile.error} />;
  if (recentPatrols.error) return <ErrorPage error={recentPatrols.error} />;
  if (scheduledPatrols.error) {
    return <ErrorPage error={scheduledPatrols.error} />;
  }
  if (sessions.error) return <ErrorPage error={sessions.error} />;

  return (
    <View
      auditPagination={auditPagination}
      audits={audits.data}
      organization={organization.data}
      profile={profile.data}
      recentPatrolPagination={recentPatrolPagination}
      recentPatrols={recentPatrols.data}
      scheduledPatrols={scheduledPatrols.data}
      sessionPagination={sessionPagination}
      sessions={sessions.data}
      setAuditPagination={setAuditPagination}
      setRecentPatrolPagination={setRecentPatrolPagination}
      setSessionPagination={setSessionPagination}
    />
  );
};

export const MyRosterView: FC<ViewProps> = ({
  organization,
  scheduledPatrols,
  recentPatrols,
  recentPatrolPagination,
  setRecentPatrolPagination,
}) => {
  const theme = useTheme();
  const smallScreen = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();

  return (
    <CenterLayout>
      <Stack direction="column" spacing={3}>
        <Card>
          <CardHeader
            action={
              organization?.allowQuickPatrol &&
              (smallScreen ? (
                <IconButton
                  aria-label="Quick Patrol"
                  component={NavLink}
                  sx={{ backgroundColor: 'secondary.main', color: 'white' }}
                  to="/patrol/quick-patrol"
                >
                  <QuickPatrolIcon />
                </IconButton>
              ) : (
                <Button
                  color="secondary"
                  component={NavLink}
                  startIcon={<QuickPatrolIcon />}
                  to="/patrol/quick-patrol"
                  variant="contained"
                >
                  Quick Patrol
                </Button>
              ))
            }
            title="Scheduled Patrols"
          />
          {scheduledPatrols.matches ? (
            smallScreen ? (
              <NarrowPatrolList
                handleSelection={(id) => navigate(`/patrol/${id}`)}
                hideWho={false}
                patrols={scheduledPatrols}
              />
            ) : (
              <WidePatrolList
                handleSelection={(id) => navigate(`/patrol/${id}`)}
                hideWho={false}
                patrols={scheduledPatrols}
              />
            )
          ) : (
            <CardContent>
              <Typography sx={{ color: 'GrayText', fontStyle: 'italic' }}>
                You have no future patrols scheduled
              </Typography>
            </CardContent>
          )}
        </Card>
        <Card>
          <CardHeader title="Recent Patrols" />
          {recentPatrols.matches ? (
            smallScreen ? (
              <NarrowPatrolList
                handleSelection={(id) => navigate(`/patrol/${id}`)}
                hideUnstartedBadge={organization?.hideUnstartedPatrolsBadge}
                hideWho={false}
                patrols={recentPatrols}
              />
            ) : (
              <WidePatrolList
                handleSelection={(id) => navigate(`/patrol/${id}`)}
                hideUnstartedBadge={organization?.hideUnstartedPatrolsBadge}
                hideWho={false}
                patrols={recentPatrols}
              />
            )
          ) : (
            <CardContent>
              <Typography sx={{ color: 'GrayText', fontStyle: 'italic' }}>
                You have not done any patrols yet
              </Typography>
            </CardContent>
          )}

          {recentPatrols.matches > recentPatrolPagination.limit ? (
            <Pagination
              count={Math.ceil(
                recentPatrols.matches / recentPatrolPagination.limit,
              )}
              onChange={(_, page) => {
                setRecentPatrolPagination({
                  limit: recentPatrolPagination.limit,
                  skip: recentPatrolPagination.limit * (page - 1),
                });
              }}
              page={
                Math.floor(
                  recentPatrolPagination.skip / recentPatrolPagination.limit,
                ) + 1
              }
              showFirstButton
              showLastButton
              sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}
            />
          ) : undefined}
        </Card>
      </Stack>
    </CenterLayout>
  );
};
